import { useState } from "react";
import { F, D } from "../../../styles/palette";
import { DC, DCk } from "../../shared";
import { validateDocument, validatePhone, DEPARTAMENTOS_CO, TIPOS_DOCUMENTO, EPS_LIST } from "../../../utils/validators";
import { CONSENT_DATOS } from "../../../utils/consentText";
import { ISL } from "../../../data/islStages";

const TI = ({ label, value, onChange, type, required, error, placeholder, multiline }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>
      {label} {required && <span style={{ color: "#f87171" }}>*</span>}
    </label>
    {multiline ? (
      <textarea value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3}
        style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${error ? "#f87171" : "rgba(100,116,139,0.2)"}`, background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 14, fontFamily: F.b, boxSizing: "border-box", resize: "vertical" }} />
    ) : (
      <input type={type || "text"} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${error ? "#f87171" : "rgba(100,116,139,0.2)"}`, background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 14, fontFamily: F.b, boxSizing: "border-box" }} />
    )}
    {error && <p style={{ color: "#f87171", fontSize: 11, margin: "4px 0 0" }}>{error}</p>}
  </div>
);

const SI = ({ label, value, onChange, options, required, error, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>
      {label} {required && <span style={{ color: "#f87171" }}>*</span>}
    </label>
    <select value={value || ""} onChange={e => onChange(e.target.value)}
      style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${error ? "#f87171" : "rgba(100,116,139,0.2)"}`, background: "rgba(30,41,59,0.8)", color: value ? "#e2e8f0" : "#64748b", fontSize: 14, fontFamily: F.b, boxSizing: "border-box" }}>
      <option value="">{placeholder || "Seleccionar..."}</option>
      {options.map(o => typeof o === "string"
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
    {error && <p style={{ color: "#f87171", fontSize: 11, margin: "4px 0 0" }}>{error}</p>}
  </div>
);

const ToggleGroup = ({ label, options, value, onChange, required }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>
      {label} {required && <span style={{ color: "#f87171" }}>*</span>}
    </label>
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {options.map(o => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        const active = value === v;
        return (
          <button key={v} type="button" onClick={() => onChange(v)}
            style={{ padding: "7px 14px", borderRadius: 7, border: `1px solid ${active ? D.ac : "rgba(100,116,139,0.15)"}`, background: active ? "rgba(45,212,191,0.1)" : "transparent", color: active ? D.ac : "#64748b", fontSize: 12, cursor: "pointer", fontWeight: active ? 700 : 500, fontFamily: F.b }}>
            {l}
          </button>
        );
      })}
    </div>
  </div>
);

const MIEMBROS = ["MSI", "MSD", "MII", "MID", "Bilateral superior", "Bilateral inferior"];
const ETIOLOGIAS = ["Primario", "Secundario oncol\u00f3gico", "Secundario no oncol\u00f3gico"];

export default function PatientForm({ user, addPatient, onCancel, onSaved, initialData, onUpdate, editMode }) {
  const [form, setForm] = useState(initialData || {
    tipoDocumento: "", numeroDocumento: "", primerNombre: "", segundoNombre: "", primerApellido: "", segundoApellido: "",
    fechaNacimiento: "", sexo: "", direccion: "", ciudad: "", departamento: "", telefono: "", email: "",
    eps: "", tipoAfiliacion: "",
    contactoEmergencia: { nombre: "", parentesco: "", telefono: "" },
    etiologia: "", miembroAfectado: "", estadioISL: null, fechaDiagnostico: "", antecedentesOncologicos: "",
  });
  const [consentDatos, setConsentDatos] = useState(editMode ? true : false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const setCE = (key, val) => setForm(p => ({ ...p, contactoEmergencia: { ...p.contactoEmergencia, [key]: val } }));

  const validate = () => {
    const e = {};
    if (!form.tipoDocumento) e.tipoDocumento = "Requerido";
    const docErr = validateDocument(form.tipoDocumento, form.numeroDocumento);
    if (docErr) e.numeroDocumento = docErr;
    if (!form.primerNombre?.trim()) e.primerNombre = "Requerido";
    if (!form.primerApellido?.trim()) e.primerApellido = "Requerido";
    if (!form.fechaNacimiento) e.fechaNacimiento = "Requerido";
    if (!form.sexo) e.sexo = "Requerido";
    if (!form.departamento) e.departamento = "Requerido";
    const phErr = validatePhone(form.telefono);
    if (phErr) e.telefono = phErr;
    if (!form.eps) e.eps = "Requerido";
    if (!form.tipoAfiliacion) e.tipoAfiliacion = "Requerido";
    if (!form.etiologia) e.etiologia = "Requerido";
    if (!form.miembroAfectado) e.miembroAfectado = "Requerido";
    if (form.estadioISL == null) e.estadioISL = "Seleccione un estadio";
    if (!consentDatos && !editMode) e.consent = "Debe aceptar el consentimiento";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editMode && onUpdate) {
        await onUpdate(form);
      } else {
        await addPatient(form);
      }
      setSuccess(true);
      setTimeout(() => onSaved(), 1200);
    } catch (err) {
      setErrors({ _general: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <DC>
        <div style={{ textAlign: "center", padding: 30 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>&#9989;</div>
          <h3 style={{ color: "#4ade80", fontSize: 18, margin: 0, fontFamily: F.b }}>{editMode ? "Paciente actualizado" : "Paciente registrado exitosamente"}</h3>
        </div>
      </DC>
    );
  }

  return (
    <div>
      <button onClick={onCancel} style={{ background: "none", border: "none", color: D.ac, cursor: "pointer", fontSize: 13, fontFamily: F.b, marginBottom: 12, padding: 0 }}>
        &larr; {editMode ? "Cancelar edici\u00f3n" : "Volver a pacientes"}
      </button>
      <h3 style={{ color: "#e0f2fe", fontSize: 17, margin: "0 0 14px", fontFamily: F.dm }}>{editMode ? "Editar Paciente" : "Nuevo Paciente"}</h3>

      {errors._general && <p style={{ color: "#f87171", fontSize: 13, marginBottom: 10 }}>{errors._general}</p>}

      {/* Identificaci\u00f3n */}
      <DC title="Identificaci\u00f3n" accent="#0284c7">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <SI label="Tipo de documento" value={form.tipoDocumento} onChange={v => set("tipoDocumento", v)} options={TIPOS_DOCUMENTO} required error={errors.tipoDocumento} />
          <TI label="N\u00famero de documento" value={form.numeroDocumento} onChange={v => set("numeroDocumento", v)} required error={errors.numeroDocumento} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TI label="Primer nombre" value={form.primerNombre} onChange={v => set("primerNombre", v)} required error={errors.primerNombre} />
          <TI label="Segundo nombre" value={form.segundoNombre} onChange={v => set("segundoNombre", v)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TI label="Primer apellido" value={form.primerApellido} onChange={v => set("primerApellido", v)} required error={errors.primerApellido} />
          <TI label="Segundo apellido" value={form.segundoApellido} onChange={v => set("segundoApellido", v)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TI label="Fecha de nacimiento" value={form.fechaNacimiento} onChange={v => set("fechaNacimiento", v)} type="date" required error={errors.fechaNacimiento} />
          <ToggleGroup label="Sexo" options={["M", "F", "I"]} value={form.sexo} onChange={v => set("sexo", v)} required />
        </div>
      </DC>

      {/* Contacto */}
      <DC title="Contacto" accent="#8b5cf6">
        <TI label="Direcci\u00f3n" value={form.direccion} onChange={v => set("direccion", v)} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TI label="Ciudad" value={form.ciudad} onChange={v => set("ciudad", v)} />
          <SI label="Departamento" value={form.departamento} onChange={v => set("departamento", v)} options={DEPARTAMENTOS_CO} required error={errors.departamento} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TI label="Tel\u00e9fono" value={form.telefono} onChange={v => set("telefono", v)} required error={errors.telefono} placeholder="3001234567" />
          <TI label="Email" value={form.email} onChange={v => set("email", v)} type="email" placeholder="opcional" />
        </div>
      </DC>

      {/* Aseguramiento */}
      <DC title="Aseguramiento" accent="#ca8a04">
        <SI label="EPS" value={form.eps} onChange={v => set("eps", v)} options={EPS_LIST} required error={errors.eps} />
        <ToggleGroup label="Tipo de afiliaci\u00f3n" options={["Contributivo", "Subsidiado", "Vinculado", "Especial"]} value={form.tipoAfiliacion} onChange={v => set("tipoAfiliacion", v)} required />
      </DC>

      {/* Contacto de emergencia */}
      <DC title="Contacto de Emergencia" accent="#64748b">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <TI label="Nombre" value={form.contactoEmergencia.nombre} onChange={v => setCE("nombre", v)} />
          <TI label="Parentesco" value={form.contactoEmergencia.parentesco} onChange={v => setCE("parentesco", v)} />
          <TI label="Tel\u00e9fono" value={form.contactoEmergencia.telefono} onChange={v => setCE("telefono", v)} />
        </div>
      </DC>

      {/* Datos cl\u00ednicos linfedema */}
      <DC title="Datos Cl\u00ednicos Linfedema" accent="#dc2626">
        <SI label="Etiolog\u00eda" value={form.etiologia} onChange={v => set("etiologia", v)} options={ETIOLOGIAS} required error={errors.etiologia} />
        <SI label="Miembro afectado" value={form.miembroAfectado} onChange={v => set("miembroAfectado", v)} options={MIEMBROS} required error={errors.miembroAfectado} />

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 8, fontFamily: F.b }}>
            Estadio ISL <span style={{ color: "#f87171" }}>*</span>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {ISL.map((st, i) => {
              const active = form.estadioISL === i;
              return (
                <div key={i} onClick={() => set("estadioISL", i)}
                  style={{ padding: 12, borderRadius: 10, cursor: "pointer", border: `2px solid ${active ? st.c : "rgba(100,116,139,0.1)"}`, background: active ? `${st.c}15` : D.card }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: st.c, fontFamily: F.m }}>{st.s}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{st.n}</div>
                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 3 }}>{st.desc}</div>
                </div>
              );
            })}
          </div>
          {errors.estadioISL && <p style={{ color: "#f87171", fontSize: 11, margin: "4px 0 0" }}>{errors.estadioISL}</p>}
        </div>

        <TI label="Fecha de diagn\u00f3stico" value={form.fechaDiagnostico} onChange={v => set("fechaDiagnostico", v)} type="date" />
        <TI label="Antecedentes oncol\u00f3gicos" value={form.antecedentesOncologicos} onChange={v => set("antecedentesOncologicos", v)} multiline placeholder="Descripci\u00f3n de antecedentes oncol\u00f3gicos..." />
      </DC>

      {/* Consentimiento datos personales */}
      {!editMode && (
        <DC title="Consentimiento Datos Personales" accent="#dc2626">
          <div style={{ maxHeight: 200, overflowY: "auto", background: "rgba(30,41,59,0.6)", padding: 14, borderRadius: 8, marginBottom: 12, border: "1px solid rgba(100,116,139,0.1)" }}>
            <pre style={{ color: "#cbd5e1", fontSize: 11, fontFamily: F.b, whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.6 }}>{CONSENT_DATOS}</pre>
          </div>
          <DCk
            label="He le\u00eddo y acepto el tratamiento de mis datos personales (Ley 1581/2012)"
            checked={consentDatos}
            onChange={() => setConsentDatos(!consentDatos)}
          />
          {errors.consent && <p style={{ color: "#f87171", fontSize: 11, marginTop: 4 }}>{errors.consent}</p>}
        </DC>
      )}

      {/* Botones */}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={onCancel}
          style={{ padding: "10px 22px", borderRadius: 8, border: `1px solid ${D.bdr}`, background: "transparent", color: "#94a3b8", fontSize: 13, cursor: "pointer", fontFamily: F.b }}>
          Cancelar
        </button>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: "10px 22px", borderRadius: 8, border: "none", background: saving ? "#64748b" : D.ac, color: "#020617", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: F.b }}>
          {saving ? "Guardando..." : editMode ? "Actualizar Paciente" : "Guardar Paciente"}
        </button>
      </div>
    </div>
  );
}
